/**
 * Zod schema + types for the Letterbox content file.
 *
 * Content lives at: packages/client/public/letters/letters.json, served at
 * `/letters/letters.json` (same shape of arrangement as `/dialogue/*.json`;
 * see src/ui/dialogue.schema.ts for the precedent).
 *
 * A letter is a small authored piece of prose from someone in the world. It
 * carries no mechanics: no objectives, no rewards, no coordinates, no travel.
 * `smallGift` is flavor only — a pressed flower, a line of a song — and never
 * grants an item or a stat.
 *
 * ```json
 * {
 *   "letters": [
 *     {
 *       "id": "mira_marigolds",
 *       "from": "Herbalist Mira",
 *       "body": "The marigolds came up tall this season...",
 *       "condition": { "minDay": 4 },
 *       "smallGift": { "name": "a pressed marigold" }
 *     }
 *   ]
 * }
 * ```
 */

import { z } from 'zod';
import { RealmId } from '@game/shared';

/**
 * Gate on when a letter may arrive. Absent condition = eligible from Day 1.
 *
 * `realm` is honoured by the delivery rules but no authored letter uses it
 * yet: the client has no realm signal on the HUD bus, so a realm-gated letter
 * would be stranded. When a realm reaches HudState, pass it into
 * `deliverDueLetter` and this field starts working with no schema change.
 */
export const LetterConditionSchema = z.object({
  /** Earliest day number this letter may be delivered on. */
  minDay: z.number().int().min(1).optional(),
  /** Realm the traveler must be in for this letter to be eligible. */
  realm: z.nativeEnum(RealmId).optional(),
});

/** Something tucked into the envelope. Cosmetic and narrative only. */
export const SmallGiftSchema = z.object({
  /** Short noun phrase, lowercase: "a pressed marigold". */
  name: z.string().min(1),
  /** Optional second clause shown in smaller ink under the gift line. */
  note: z.string().optional(),
});

export const LetterSchema = z.object({
  /** Stable id; the persisted read/delivered state keys off this. */
  id: z.string().min(1),
  /** Who wrote it, as the player should read it: "Herbalist Mira". */
  from: z.string().min(1),
  /** Optional lowercase line under the sender, shown in the opened letter. */
  subject: z.string().optional(),
  /** The prose. Blank lines separate paragraphs. */
  body: z.string().min(1),
  /** Sign-off line. Defaults to the sender when omitted. */
  signature: z.string().optional(),
  condition: LetterConditionSchema.optional(),
  smallGift: SmallGiftSchema.optional(),
});

export const LettersFileSchema = z.object({
  /** Authored order is delivery order among equally-eligible letters. */
  letters: z.array(LetterSchema).min(1),
});

export type LetterCondition = z.infer<typeof LetterConditionSchema>;
export type SmallGift = z.infer<typeof SmallGiftSchema>;
export type Letter = z.infer<typeof LetterSchema>;
export type LettersFile = z.infer<typeof LettersFileSchema>;

/** Public URL the client fetches the content file from. */
export const LETTERS_URL = '/letters/letters.json';

/**
 * Validate raw JSON into letters. Returns an empty list (never throws) so a
 * malformed content file degrades to "no mail" rather than a broken boot.
 */
export function parseLettersFile(raw: unknown): Letter[] {
  const result = LettersFileSchema.safeParse(raw);
  if (!result.success) {
    console.warn('[letterbox] letters.json failed validation', result.error.issues);
    return [];
  }
  return result.data.letters;
}
